/**
 * O formulário do modal de lançamento: o que os campos guardam e as regras
 * que a origem aplica a cada mudança e ao salvar.
 *
 * Papel: aqui ficam `ValoresDoLancamento` — os campos como a tela os mostra —,
 * as listas de categoria e subcategoria pelo tipo (`populateCatDropdown`,
 * `populateSubCatDropdown`, l. 1861–1891), a abertura de "Novo" e de "Editar"
 * (`openAddEntry`, `openEditEntry`, l. 2252–2334), o autopreenchimento pelo
 * perfil do favorecido (`onFavorecidoChange`, l. 1905–1948), o valor final, as
 * datas personalizadas do parcelamento (l. 2227–2251), a validação na ordem da
 * origem e os avisos de `saveEntry` (l. 2343–2482).
 *
 * Como usar: toda função é pura e devolve campos novos; o modal guarda os
 * campos e chama estas funções. A validação passa pelo
 * `salvarLancamentoSchema` de `@concierge/shared`, o mesmo da API, e escolhe
 * entre as mensagens a primeira na ordem da origem: obrigatórios, valor final,
 * datas. O "hoje" de quem chama é `hojeEmIso`, em hora local (exceção 1).
 *
 * A categoria e a subcategoria gravadas que não estão na lista do tipo não
 * aparecem ao editar: o `select` mostra a primeira categoria e "— Selecionar
 * —", e salvar grava essa primeira categoria e a subcategoria vazia. Na origem
 * a subcategoria também ia vazia, mas o `select` da categoria ficava sem opção.
 */
import {
  CATEGORIA_CREDITO_MOVIMENTACAO,
  CATEGORIA_DEBITO_MOVIMENTACAO,
  type Categoria,
  FORMA_PAGAMENTO_LANCAMENTO,
  type FormaPagamentoLancamento,
  type Lancamento,
  MAXIMO_DE_PARCELAS,
  MAXIMO_DE_REPETICOES,
  MENSAGEM_DAS_DATAS,
  MENSAGEM_DO_VALOR_FINAL,
  MENSAGEM_DOS_OBRIGATORIOS,
  MINIMO_DE_PARCELAS,
  type Repeticao,
  type SalvarLancamento,
  type TipoLancamento,
  salvarLancamentoSchema,
  tipoDaCategoria,
  valorFinalDoModal,
} from '@concierge/shared';

import { lerValorDigitado, mascararCentavos } from '@/shared/lib/mascara';
import { formatarReais } from './formato';

/** O "Intervalo" do parcelamento, na ordem do `select` (l. 816–821). */
export type IntervaloDoModal = 'mensal' | 'quinzenal' | 'semanal' | 'avulso';

/** Os campos do modal, como a tela os mostra. */
export interface ValoresDoLancamento {
  favorecido: string;
  /** `AAAA-MM-DD`, ou vazio. */
  data: string;
  categoria: string;
  /** Vazio é "— Selecionar —". */
  subcategoria: string;
  tipo: TipoLancamento;
  formaPagamento: FormaPagamentoLancamento;
  /** O valor bruto, mascarado em centavos. */
  valor: string;
  descricao: string;
  /** "✓ Pago / Confirmado"; falso é "⏳ Provisionado / A pagar". */
  pago: boolean;
  desconto: string;
  acrescimo: string;
  observacao: string;
  recorrencia: boolean;
  /** "Repetir por quantos meses?", como está no campo. */
  meses: string;
  parcelar: boolean;
  /** "Número de Parcelas", como está no campo. */
  parcelas: string;
  intervalo: IntervaloDoModal;
  /** As datas personalizadas, uma por parcela, na ordem dos campos. */
  datas: string[];
}

/** Os campos que o perfil do favorecido preencheu, para o realce. */
export type CampoDoPerfil = 'tipo' | 'categoria' | 'subcategoria' | 'formaPagamento';

/** O perfil que o autopreenchimento aplica: o do favorecido ou o do padrão. */
export interface PerfilDoNome {
  categoriaPadrao: string | null;
  subcategoriaPadrao: string | null;
  formaPagamentoPadrao: FormaPagamentoLancamento | null;
}

/**
 * As categorias que o `select` oferece para um tipo.
 *
 * @param plano - O plano de contas, em ordem alfabética.
 * @param tipo - O tipo escolhido.
 * @returns As de natureza despesa ou receita, ou a única de crédito ou de
 *   débito de movimentação.
 */
export function categoriasDoTipo(plano: readonly Categoria[], tipo: TipoLancamento): string[] {
  if (tipo === 'credito_movimentacao') return [CATEGORIA_CREDITO_MOVIMENTACAO];
  if (tipo === 'debito_movimentacao') return [CATEGORIA_DEBITO_MOVIMENTACAO];
  return plano
    .filter((categoria) => categoria.natureza === tipo)
    .map((categoria) => categoria.nome);
}

/**
 * As subcategorias que o `select` oferece, depois de "— Selecionar —".
 *
 * @param plano - O plano de contas.
 * @param tipo - O tipo escolhido.
 * @param categoria - A categoria escolhida.
 * @returns Todas as da categoria, em ordem alfabética; no crédito e no débito de
 *   movimentação, só as de sistema.
 */
export function subcategoriasDoTipo(
  plano: readonly Categoria[],
  tipo: TipoLancamento,
  categoria: string,
): string[] {
  const subcategorias = plano.find((item) => item.nome === categoria)?.subcategorias ?? [];
  const movimentacao = tipo === 'credito_movimentacao' || tipo === 'debito_movimentacao';
  return subcategorias
    .filter((subcategoria) => !movimentacao || subcategoria.sistema)
    .map((subcategoria) => subcategoria.nome);
}

/**
 * Troca o tipo e refaz a categoria e a subcategoria (`onTipoChange`).
 *
 * @param valores - Os campos.
 * @param tipo - O tipo novo.
 * @param plano - O plano de contas.
 * @returns Os campos com a primeira categoria do tipo e sem subcategoria.
 */
export function comTipo(
  valores: ValoresDoLancamento,
  tipo: TipoLancamento,
  plano: readonly Categoria[],
): ValoresDoLancamento {
  return {
    ...valores,
    tipo,
    categoria: categoriasDoTipo(plano, tipo)[0] ?? '',
    subcategoria: '',
  };
}

/**
 * Escreve um valor em reais como o campo mascarado em centavos o mostra.
 *
 * @param valor - O valor em reais.
 * @returns `1.234,50`.
 */
function escritoEmCentavos(valor: number): string {
  return mascararCentavos(String(Math.round(valor * 100)));
}

/**
 * Os campos de "Novo Lançamento" (l. 2252–2282).
 *
 * @param hoje - A data de hoje, em hora local.
 * @param plano - O plano de contas.
 * @returns Despesa, hoje, Pix, "A pagar", o resto vazio, e as duas seções
 *   desmarcadas com 3 meses e 2 parcelas mensais.
 */
export function valoresDoNovo(hoje: string, plano: readonly Categoria[]): ValoresDoLancamento {
  return comTipo(
    {
      favorecido: '',
      data: hoje,
      categoria: '',
      subcategoria: '',
      tipo: 'despesa',
      formaPagamento: 'pix',
      valor: '',
      descricao: '',
      pago: false,
      desconto: '',
      acrescimo: '',
      observacao: '',
      recorrencia: false,
      meses: '3',
      parcelar: false,
      parcelas: '2',
      intervalo: 'mensal',
      datas: [],
    },
    'despesa',
    plano,
  );
}

/**
 * Os campos de "Editar Lançamento" (l. 2283–2334).
 *
 * @param lancamento - O lançamento pendente.
 * @param plano - O plano de contas.
 * @returns O gravado nos campos, com desconto e acréscimo vazios quando zero, a
 *   forma ausente como Pix, e a categoria ou a subcategoria fora da lista
 *   trocadas pela primeira opção.
 */
export function valoresDoEditado(
  lancamento: Pick<
    Lancamento,
    | 'favorecido'
    | 'data'
    | 'tipo'
    | 'categoria'
    | 'subcategoria'
    | 'descricao'
    | 'formaPagamento'
    | 'valorBruto'
    | 'desconto'
    | 'acrescimo'
    | 'pago'
    | 'observacao'
  >,
  plano: readonly Categoria[],
): ValoresDoLancamento {
  const categorias = categoriasDoTipo(plano, lancamento.tipo);
  const categoria = categorias.includes(lancamento.categoria)
    ? lancamento.categoria
    : (categorias[0] ?? '');
  const subcategoria = subcategoriasDoTipo(plano, lancamento.tipo, categoria).includes(
    lancamento.subcategoria ?? '',
  )
    ? (lancamento.subcategoria ?? '')
    : '';
  return {
    favorecido: lancamento.favorecido?.nome ?? '',
    data: lancamento.data,
    categoria,
    subcategoria,
    tipo: lancamento.tipo,
    formaPagamento: lancamento.formaPagamento ?? 'pix',
    valor: escritoEmCentavos(lancamento.valorBruto),
    descricao: lancamento.descricao,
    pago: lancamento.pago,
    desconto: lancamento.desconto > 0 ? escritoEmCentavos(lancamento.desconto) : '',
    acrescimo: lancamento.acrescimo > 0 ? escritoEmCentavos(lancamento.acrescimo) : '',
    observacao: lancamento.observacao ?? '',
    recorrencia: false,
    meses: '3',
    parcelar: false,
    parcelas: '2',
    intervalo: 'mensal',
    datas: [],
  };
}

/**
 * O perfil que um nome digitado traz.
 *
 * @param nome - O texto do campo Favorecido.
 * @param favorecidos - Os favorecidos cadastrados.
 * @param padroes - Os padrões de favorecido.
 * @returns O perfil do favorecido de nome **exatamente** igual ao texto sem os
 *   espaços das pontas, ou, não havendo, o do padrão; nulo sem nenhum dos dois.
 */
export function perfilDoNome(
  nome: string,
  favorecidos: readonly (PerfilDoNome & { nome: string })[],
  padroes: readonly (PerfilDoNome & { nome: string })[],
): PerfilDoNome | null {
  const aparado = nome.trim();
  if (aparado === '') return null;
  return (
    favorecidos.find((favorecido) => favorecido.nome === aparado) ??
    padroes.find((padrao) => padrao.nome === aparado) ??
    null
  );
}

/** O que o autopreenchimento devolve. */
export interface Autopreenchimento {
  valores: ValoresDoLancamento;
  /** Os campos trocados, que ganham o realce. */
  campos: Set<CampoDoPerfil>;
  /** Verdadeiro quando a categoria ou a forma foram aplicadas — e só então há aviso. */
  avisar: boolean;
}

/**
 * Aplica o perfil, sobrescrevendo o que estava (`onFavorecidoChange`).
 *
 * Com categoria padrão, o tipo vira o dela e a lista se refaz; a categoria e a
 * subcategoria só entram se estiverem na lista refeita; a forma entra se for
 * uma das sete.
 *
 * @param valores - Os campos.
 * @param perfil - O perfil do nome digitado.
 * @param plano - O plano de contas.
 * @returns Os campos novos, os trocados e se há aviso.
 */
export function aplicarPerfil(
  valores: ValoresDoLancamento,
  perfil: PerfilDoNome,
  plano: readonly Categoria[],
): Autopreenchimento {
  let proximos = valores;
  const campos = new Set<CampoDoPerfil>();
  let avisar = false;

  if (perfil.categoriaPadrao !== null && perfil.categoriaPadrao !== '') {
    const tipo = tipoDaCategoria(perfil.categoriaPadrao, plano) ?? 'despesa';
    proximos = comTipo(proximos, tipo, plano);
    campos.add('tipo');
    if (categoriasDoTipo(plano, tipo).includes(perfil.categoriaPadrao)) {
      proximos = { ...proximos, categoria: perfil.categoriaPadrao, subcategoria: '' };
      campos.add('categoria');
      avisar = true;
    }
    const sub = perfil.subcategoriaPadrao;
    if (
      sub !== null &&
      sub !== '' &&
      subcategoriasDoTipo(plano, proximos.tipo, proximos.categoria).includes(sub)
    ) {
      proximos = { ...proximos, subcategoria: sub };
      campos.add('subcategoria');
    }
  }

  const forma = perfil.formaPagamentoPadrao;
  if (forma !== null && FORMA_PAGAMENTO_LANCAMENTO.includes(forma)) {
    proximos = { ...proximos, formaPagamento: forma };
    campos.add('formaPagamento');
    avisar = true;
  }

  return { valores: proximos, campos, avisar };
}

/**
 * Os campos do editado que coincidem com o perfil, para o realce ao abrir
 * (l. 2321–2328).
 *
 * @param valores - Os campos do editado.
 * @param perfil - O perfil do favorecido do editado, ou nulo.
 * @param plano - O plano de contas.
 * @returns Os campos iguais aos do perfil.
 */
export function camposIguaisAoPerfil(
  valores: ValoresDoLancamento,
  perfil: PerfilDoNome | null,
  plano: readonly Categoria[],
): Set<CampoDoPerfil> {
  const campos = new Set<CampoDoPerfil>();
  if (perfil === null) return campos;
  const categoria = perfil.categoriaPadrao ?? '';
  if (categoria !== '' && tipoDaCategoria(categoria, plano) === valores.tipo)
    campos.add('tipo');
  if (categoria !== '' && valores.categoria === categoria) campos.add('categoria');
  if (
    (perfil.subcategoriaPadrao ?? '') !== '' &&
    valores.subcategoria === perfil.subcategoriaPadrao
  ) {
    campos.add('subcategoria');
  }
  if (
    perfil.formaPagamentoPadrao !== null &&
    valores.formaPagamento === perfil.formaPagamentoPadrao
  ) {
    campos.add('formaPagamento');
  }
  return campos;
}

/**
 * O "Valor Final" à vista (`updateValorFinal`, l. 2335–2342).
 *
 * @param valores - Os campos.
 * @returns Bruto − desconto + acréscimo em reais, ou "—" enquanto não passa de
 *   zero.
 */
export function valorFinalEscrito(
  valores: Pick<ValoresDoLancamento, 'valor' | 'desconto' | 'acrescimo'>,
): string {
  const final = valorFinalDoModal(
    lerValorDigitado(valores.valor),
    lerValorDigitado(valores.desconto),
    lerValorDigitado(valores.acrescimo),
  );
  return final === null ? '—' : formatarReais(final);
}

/**
 * Lê um número inteiro de um campo `number`, dentro dos limites dele.
 *
 * @param texto - O campo.
 * @param minimo - O `min` do campo, que vale também para o vazio.
 * @param maximo - O `max` do campo.
 * @returns O número, preso entre o mínimo e o máximo.
 */
function inteiroDoCampo(texto: string, minimo: number, maximo: number): number {
  const lido = Number.parseInt(texto, 10);
  return Math.min(Math.max(Number.isNaN(lido) ? minimo : lido, minimo), maximo);
}

/**
 * Refaz as datas personalizadas quando o número de parcelas muda
 * (`buildCustomDates`, l. 2227–2251).
 *
 * @param atuais - As datas que já estão nos campos.
 * @param parcelas - O "Número de Parcelas", como está no campo.
 * @param dataDoLancamento - A Data do lançamento, ou vazio.
 * @param hoje - A data de hoje, para quando a Data está vazia.
 * @returns Uma data por parcela: a que já estava na mesma posição, ou a Data do
 *   lançamento (ou hoje).
 */
export function datasPersonalizadas(
  atuais: readonly string[],
  parcelas: string,
  dataDoLancamento: string,
  hoje: string,
): string[] {
  const quantas = inteiroDoCampo(parcelas, MINIMO_DE_PARCELAS, MAXIMO_DE_PARCELAS);
  const inicio = dataDoLancamento === '' ? hoje : dataDoLancamento;
  return Array.from({ length: quantas }, (_, indice) => {
    const atual = atuais[indice] ?? '';
    return atual === '' ? inicio : atual;
  });
}

/**
 * A repetição pedida pelas duas seções de baixo.
 *
 * @param valores - Os campos.
 * @returns Nula, a recorrência ou o parcelamento.
 */
function repeticaoDoModal(valores: ValoresDoLancamento): Repeticao {
  if (valores.recorrencia) {
    return {
      tipo: 'recorrencia',
      meses: inteiroDoCampo(valores.meses, 1, MAXIMO_DE_REPETICOES),
    };
  }
  if (!valores.parcelar) return null;
  if (valores.intervalo === 'avulso') {
    return {
      tipo: 'parcelamento',
      intervalo: 'avulso',
      datas: valores.datas.filter((data) => data !== ''),
    };
  }
  return {
    tipo: 'parcelamento',
    intervalo: valores.intervalo,
    parcelas: inteiroDoCampo(valores.parcelas, MINIMO_DE_PARCELAS, MAXIMO_DE_PARCELAS),
  };
}

/** A ordem em que a origem confere o modal (l. 2350–2351, 2412). */
const ORDEM_DAS_MENSAGENS = [
  MENSAGEM_DOS_OBRIGATORIOS,
  MENSAGEM_DO_VALOR_FINAL,
  MENSAGEM_DAS_DATAS,
];

/** O resultado da validação do modal. */
export type ModalValidado =
  { valido: true; corpo: SalvarLancamento } | { valido: false; mensagem: string };

/**
 * Valida o modal pelo schema compartilhado e devolve o corpo da gravação.
 *
 * @param valores - Os campos.
 * @param residenciaId - A casa do caixa.
 * @returns O corpo de `POST`/`PUT /financas/lancamentos`, ou a mensagem que a
 *   origem mostraria primeiro.
 */
export function validarModal(
  valores: ValoresDoLancamento,
  residenciaId: string,
): ModalValidado {
  const resultado = salvarLancamentoSchema.safeParse({
    lancamento: {
      residenciaId,
      favorecidoNome: valores.favorecido,
      data: valores.data,
      tipo: valores.tipo,
      categoria: valores.categoria,
      subcategoria: valores.subcategoria,
      descricao: valores.descricao,
      formaPagamento: valores.formaPagamento,
      valorBruto: lerValorDigitado(valores.valor),
      desconto: lerValorDigitado(valores.desconto),
      acrescimo: lerValorDigitado(valores.acrescimo),
      pago: valores.pago,
      observacao: valores.observacao,
    },
    repeticao: repeticaoDoModal(valores),
  });
  if (resultado.success) return { valido: true, corpo: resultado.data };
  const mensagens = resultado.error.issues.map((problema) => problema.message);
  const mensagem =
    ORDEM_DAS_MENSAGENS.find((conhecida) => mensagens.includes(conhecida)) ??
    mensagens[0] ??
    MENSAGEM_DOS_OBRIGATORIOS;
  return { valido: false, mensagem };
}

/**
 * O aviso depois de gravar pelo modal.
 *
 * @param repeticao - A repetição gravada.
 * @param editando - Verdadeiro quando o modal editou um lançamento.
 * @returns O texto da origem para o lançamento simples, a recorrência ou o
 *   parcelamento, no singular e no plural.
 */
export function avisoDoSalvar(repeticao: Repeticao, editando: boolean): string {
  if (repeticao === null)
    return editando ? '✓ Lançamento atualizado' : '✓ Lançamento adicionado';
  if (repeticao.tipo === 'recorrencia') {
    const plural = repeticao.meses > 1 ? 's' : '';
    return `✓ Lançamento + ${String(repeticao.meses)} recorrência${plural} provisionada${plural}`;
  }
  const quantas = 'datas' in repeticao ? repeticao.datas.length : repeticao.parcelas;
  return `✓ ${String(quantas)} parcelas ${editando ? 'atualizadas/criadas' : 'criadas'}`;
}
